import React, { useCallback, useMemo, useEffect, useRef, useContext } from 'react';
import { ThemeProvider } from 'styled-components';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import { TextAreaInputProps } from './types';
import {
  CharCounter,
  ErrorMessage,
  HelperContainer,
  HelperText,
  LengthMessage,
  TextArea,
  TextAreaContainer,
  TextAreaWrapper,
} from './styles';
import BasicIcon from '../BasicIcon/BasicIcon';

const TextAreaInput: React.FC<TextAreaInputProps> = (props) => {
  const {
    input,
    meta,
    placeholder,
    onChange,
    value,
    readOnly,
    disabled,
    maxLength,
    maxLengthMessage,
    helperText,
    testId,
    rows = 3,
    onBlur,
    onFocus,
    onMouseDown,
  } = props;

  const { actualTheme, isMobile } = useContext(ThemeContext);
  const hasError = useMemo(() => meta && meta.invalid && meta.touched, [meta]);
  const inputValue = useMemo(() => value ?? input.value, [input.value, value]);
  const isMaxLength = useMemo(() => maxLength > 0 && inputValue.length >= maxLength, [
    inputValue.length,
    maxLength,
  ]);

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (onChange) {
      onChange(input.value);
    }
  }, [input.value, onChange]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (input) {
        input.onChange(event.target.value);
      }
      if (onChange) {
        onChange(event.target.value);
      }
    },
    [input, onChange],
  );

  const handleBlur = useCallback(() => {
    if (!disabled && !readOnly) {
      if (onBlur) onBlur();
      if (input?.onBlur) input.onBlur();
    }
  }, [input, onBlur, disabled, readOnly]);

  const handleFocus = useCallback(() => {
    if (!disabled && !readOnly) {
      if (onFocus) onFocus();
      if (input?.onFocus) input.onFocus();
    }
  }, [input, onFocus, disabled, readOnly]);

  const handleMouseDown = useCallback(
    () => onMouseDown && !disabled && !readOnly && onMouseDown(),
    [onMouseDown, disabled, readOnly],
  );

  const showHelperOrError = useCallback(() => {
    if (hasError) {
      return (
        <>
          <ErrorMessage data-testid={`${testId}-error`}>{meta.error}</ErrorMessage>
        </>
      );
    }

    if (isMaxLength && maxLengthMessage) {
      return (
        <>
          <LengthMessage disabled={disabled} data-testid={`${testId}-lenght-message`}>
            {maxLengthMessage}
          </LengthMessage>
        </>
      );
    }

    if (helperText) return <HelperText disabled={disabled}>{helperText}</HelperText>;

    return null;
  }, [hasError, isMaxLength, maxLengthMessage, helperText, disabled, meta.error, testId]);

  return (
    <ThemeProvider theme={actualTheme}>
      <>
        <TextAreaContainer
          hasError={hasError}
          rows={rows}
          maxLength={isMaxLength}
          disabled={disabled}
        >
          <TextAreaWrapper>
            <TextArea
              isMobile={isMobile}
              hasError={hasError}
              placeholder={placeholder}
              readOnly={readOnly || disabled}
              {...input}
              disabled={disabled}
              value={inputValue}
              onChange={handleChange}
              data-testid={testId}
              ref={textAreaRef}
              rows={rows}
              maxLength={maxLength}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onMouseDown={handleMouseDown}
            />
          </TextAreaWrapper>
        </TextAreaContainer>
        <HelperContainer>
          {(helperText || hasError || isMaxLength) && showHelperOrError()}
          {maxLength > 0 && (
            <CharCounter
              hasError={hasError}
              disabled={disabled}
              data-testid={`${testId}-char-count`}
            >
              {`${inputValue.length}/${maxLength}`}
            </CharCounter>
          )}
        </HelperContainer>
      </>
    </ThemeProvider>
  );
};

export default TextAreaInput;
